import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  UserQualificationV2,
  UserQualificationV2WithRelation,
} from '@wilson/interfaces';
import {
  isInfiniteValidQualificationDateString,
  UpdateUserQualificationDto,
  UserQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { isEqual } from 'date-fns';
import { isEqual as _isEqual, cloneDeep } from 'lodash';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { UserQualificationsV2AttachmentGatewayService } from '../../../../gateways/user-qualifications-v2-attachment-gateway.service';
import { UserQualificationsV2ListService } from '../../../../service/user-qualifications-v2-list.service';
import { BulkQualificationsAttachmentSectionComponent } from '../../shared-drawer-components/bulk-qualifications-attachment-section/bulk-qualifications-attachment-section.component';
import { BulkQualificationsDatesAndApprovalSectionComponent } from '../../shared-drawer-components/bulk-qualifications-dates-and-approval-section/bulk-qualifications-dates-and-approval-section.component';
import { BulkQualificationsFooterSectionComponent } from '../../shared-drawer-components/bulk-qualifications-footer-section/bulk-qualifications-footer-section.component';
import { BulkQualificationsNotesSectionComponent } from '../../shared-drawer-components/bulk-qualifications-notes-section/bulk-qualifications-notes-section.component';
import { BulkEditQualificationsService } from '../bulk-edit-qualifications.service';
import { BulkEditQualificationsSelectionListComponent } from './bulk-edit-qualifications-selection-list/bulk-edit-qualifications-selection-list.component';
import { BulkEditQualificationSelectionHasMixedValues } from '../../../../types';

@Component({
  selector: 'wilson-bulk-edit-qualifications-drawer',
  imports: [
    BulkEditQualificationsSelectionListComponent,
    BulkQualificationsAttachmentSectionComponent,
    BulkQualificationsDatesAndApprovalSectionComponent,
    BulkQualificationsFooterSectionComponent,
    BulkQualificationsNotesSectionComponent,
    CommonModule,
    FormsModule,
    NzDrawerModule,
    NzFormModule,
    TranslatePipe,
  ],
  templateUrl: './bulk-edit-qualifications-drawer.component.html',
  styleUrl: './bulk-edit-qualifications-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkEditQualificationsDrawerComponent
  implements OnInit, OnDestroy
{
  private readonly bulkEditQualificationsService = inject(
    BulkEditQualificationsService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly userQualificationsV2AttachmentGatewayService = inject(
    UserQualificationsV2AttachmentGatewayService,
  );
  private readonly userQualificationsV2ListService = inject(
    UserQualificationsV2ListService,
  );

  private destroyed$ = new Subject<void>();

  protected savingDrawer$ = new BehaviorSubject<boolean>(false);
  protected bulkEditDrawerVisible$ =
    this.bulkEditQualificationsService.bulkEditDrawerVisible$;

  protected acquiredAtFormControl = new FormControl<Date | null>(null);
  protected validFromFormControl = new FormControl<Date | null>(null);
  protected validUntilFormControl = new FormControl<Date | null>(null);
  protected infiniteValidFormControl = new FormControl<boolean>(false);
  protected confirmedByEmployeeFormControl = new FormControl<boolean>(false);
  protected notesFormControl = new FormControl<string>('');

  protected selectionHasMixedValues: BulkEditQualificationSelectionHasMixedValues =
    {
      acquiredAt: false,
      validFrom: false,
      validTill: false,
      confirmedByUser: false,
      notes: false,
    };
  protected attachmentsList: NzUploadFile[] = [];
  private originalAttachmentsList: NzUploadFile[] = [];

  ngOnInit(): void {
    combineLatest([
      this.bulkEditQualificationsService.bulkEditDrawerVisible$.pipe(
        startWith(false),
      ),
      this.userQualificationsV2ListService.selectedQualifications$.pipe(
        startWith([]),
      ),
    ])
      .pipe(
        takeUntil(this.destroyed$),
        pairwise(),
        map(([[oldVisible, oldSelection], [visible, selection]]) => {
          const visibleChanged = oldVisible !== visible;
          const selectionChanged = !_isEqual(oldSelection, selection);
          return {
            visibleChanged,
            selectionChanged,
            visible,
            selection,
          };
        }),
        filter((change) => !change.visibleChanged && change.selectionChanged),
        tap(() => {
          this.resetDrawerData();
        }),
        filter((change) => change.selection.length > 0),
        switchMap((change) => {
          return this.bulkEditQualificationsService.bulkEditDrawerVisible$.pipe(
            filter((visible) => visible),
            take(1),
            switchMap(() => {
              return forkJoin(
                change.selection.map((item) =>
                  // replace with cache layer (requires cache invalidation)
                  this.userQualificationsGateway
                    .getUserQualificationsDetails(item.id, item.userId)
                    .pipe(map((details) => details.data[0])),
                ),
              );
            }),
          );
        }),
        tap((selectionWithRelations) => {
          this.setFormControlValidators(selectionWithRelations);
          this.selectionHasMixedValues = this.checkForMixedValues(
            selectionWithRelations,
          );
          this.setAttachmentsValues(selectionWithRelations);
          this.setFormValuesBasedOnMixedStatus(selectionWithRelations[0]);
          this.markFormAsPristine();
        }),
      )
      .subscribe();
    this.setupSavingDrawerListener();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  protected closeBulkCreateEditDrawer(): void {
    this.bulkEditQualificationsService.setBulkEditDrawerVisible(false);
  }

  protected cancelDrawer(): void {
    this.bulkEditQualificationsService.triggerBulkEditCanceled();
  }

  protected saveDrawer(): void {
    this.savingDrawer$.next(true);
    this.userQualificationsV2ListService.selectedQualifications$
      .pipe(
        take(1),
        switchMap((selection) => {
          const bulkDto: UpdateUserQualificationDto[] = [];
          const requests: Observable<any>[] = [];
          selection.forEach((userQualification) => {
            const payload: UpdateUserQualificationDto = {
              id: userQualification.id,
            };
            if (
              this.acquiredAtFormControl.dirty &&
              this.acquiredAtFormControl.value
            ) {
              payload.acquiredAt =
                this.acquiredAtFormControl.value.toISOString();
            }
            if (
              this.validFromFormControl.dirty &&
              this.validFromFormControl.value
            ) {
              payload.validFrom = this.validFromFormControl.value.toISOString();
            }
            if (
              (this.validUntilFormControl.dirty ||
                this.infiniteValidFormControl.dirty) &&
              this.validUntilFormControl.value
            ) {
              payload.validTill =
                this.validUntilFormControl.value.toISOString();
            }
            if (
              this.confirmedByEmployeeFormControl.dirty &&
              this.confirmedByEmployeeFormControl.value !== null
            ) {
              payload.confirmedByUser =
                this.confirmedByEmployeeFormControl.value;
            }
            if (
              this.notesFormControl.dirty &&
              this.notesFormControl.value !== null
            ) {
              payload.notes = this.notesFormControl.value;
            }

            if (Object.keys(payload).length > 1) {
              bulkDto.push(payload);
            }
          });

          if (bulkDto.length) {
            requests.push(
              this.userQualificationsGateway.updateUserQualifications(bulkDto),
            );
          }

          if (this.haveAttachmentsChanged()) {
            const attachmentsPayload =
              this.prepareAttachmentsPayloads(selection);
            if (attachmentsPayload.toAdd) {
              requests.push(
                this.userQualificationsV2AttachmentGatewayService.addAttachments(
                  attachmentsPayload.toAdd.addedAttachments,
                  attachmentsPayload.toAdd.addedAttachmentsUserQualificationIds,
                ),
              );
            }
            if (attachmentsPayload.toDelete) {
              requests.push(
                this.userQualificationsV2AttachmentGatewayService.deleteAttachments(
                  attachmentsPayload.toDelete.removedAttachmentsIds,
                ),
              );
            }
          }

          if (!requests.length) {
            return of(undefined).pipe(
              tap(() => {
                this.handleFailedDrawerSave();
              }),
            );
          } else {
            return forkJoin(requests).pipe(
              tap(() => {
                this.handleSuccessfulDrawerSave();
              }),
              catchError(() => {
                return this.handleFailedDrawerSave();
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  public haveAttachmentsChanged(): boolean {
    return (
      this.attachmentsList.some(
        (att) => !att.status || (att.status === 'removed' && att['id']),
      ) || this.attachmentsList.length < this.originalAttachmentsList.length
    );
  }

  private prepareAttachmentsPayloads(selection: UserQualificationV2[]): {
    toAdd?: {
      addedAttachments: NzUploadFile[];
      addedAttachmentsUserQualificationIds: string[];
    };
    toDelete?: {
      removedAttachmentsIds: string[];
    };
  } {
    let attachmentsPayloads = {};
    const addedAttachments = this.attachmentsList.filter((att) => !att.status);
    const addedAttachmentsUserQualificationIds = selection.map((s) => s.id);
    const removedAttachmentsStillInList = this.attachmentsList.filter(
      (att) => att.status === 'removed' && att['id'],
    );
    const removedAttachmentsOnlyInOriginal =
      this.originalAttachmentsList.filter(
        (oAtt) => !this.attachmentsList.some((att) => att['id'] === oAtt['id']),
      );
    const removedAttachmentsIds: string[] = removedAttachmentsStillInList
      .map((rAtt) => rAtt['id'])
      .concat(removedAttachmentsOnlyInOriginal.map((rAttO) => rAttO['id']));
    if (addedAttachments.length) {
      attachmentsPayloads = {
        toAdd: {
          addedAttachments,
          addedAttachmentsUserQualificationIds,
        },
      };
    }
    if (removedAttachmentsIds.length) {
      attachmentsPayloads = {
        ...attachmentsPayloads,
        toDelete: {
          removedAttachmentsIds,
        },
      };
    }
    return attachmentsPayloads;
  }

  private resetDrawerData(): void {
    this.resetForm();
    this.attachmentsList = [];
    this.originalAttachmentsList = [];
  }

  private resetForm(): void {
    this.acquiredAtFormControl.reset(null);
    this.validFromFormControl.reset(null);
    this.validUntilFormControl.reset(null);
    this.infiniteValidFormControl.reset(null);
    this.confirmedByEmployeeFormControl.reset(null);
    this.notesFormControl.reset(null);
  }

  private markFormAsPristine(): void {
    this.acquiredAtFormControl.markAsPristine();
    this.validFromFormControl.markAsPristine();
    this.validUntilFormControl.markAsPristine();
    this.infiniteValidFormControl.markAsPristine();
    this.confirmedByEmployeeFormControl.markAsPristine();
    this.notesFormControl.markAsPristine();
  }

  private setFormControlValidators(
    qualifications: UserQualificationV2[],
  ): void {
    if (qualifications.length > 1) {
      this.acquiredAtFormControl.clearAsyncValidators();
      this.acquiredAtFormControl.clearValidators();
      this.validFromFormControl.clearAsyncValidators();
      this.validFromFormControl.clearValidators();
      this.validUntilFormControl.clearAsyncValidators();
      this.validUntilFormControl.clearValidators();
    } else {
      this.acquiredAtFormControl.addValidators([Validators.required]);
      this.validFromFormControl.addValidators([Validators.required]);
      this.validUntilFormControl.addValidators([Validators.required]);
    }

    this.acquiredAtFormControl.updateValueAndValidity();
    this.validFromFormControl.updateValueAndValidity();
    this.validUntilFormControl.updateValueAndValidity();
  }

  private checkForMixedValues(
    qualifications: UserQualificationV2WithRelation[],
  ): BulkEditQualificationSelectionHasMixedValues {
    let hasMixedValuesResult: BulkEditQualificationSelectionHasMixedValues = {
      acquiredAt: false,
      validFrom: false,
      validTill: false,
      confirmedByUser: false,
      notes: false,
    };
    if (qualifications.length > 1) {
      const first = qualifications[0];
      const firstAcquiredAt = new Date(first.acquiredAt);
      const firstValidFrom = new Date(first.validFrom);
      const firstValidTill = new Date(first.validTill);
      const firstConfirmedByUser = first.confirmedByUser;
      const firstNotes = first.notes;

      const acquiredAtMixed = qualifications.some(
        (q) => !isEqual(new Date(q.acquiredAt), firstAcquiredAt),
      );
      const validFromMixed = qualifications.some(
        (q) => !isEqual(new Date(q.validFrom), firstValidFrom),
      );
      const validTillMixed = qualifications.some(
        (q) => !isEqual(new Date(q.validTill), firstValidTill),
      );
      const confirmedByUserMixed = qualifications.some(
        (q) => q.confirmedByUser !== firstConfirmedByUser,
      );
      const notesMixed = qualifications.some(
        (q) => (q.notes || '') !== (firstNotes || ''),
      );

      hasMixedValuesResult = {
        acquiredAt: acquiredAtMixed,
        validFrom: validFromMixed,
        validTill: validTillMixed,
        confirmedByUser: confirmedByUserMixed,
        notes: notesMixed,
      };
    }

    return hasMixedValuesResult;
  }

  private setFormValuesBasedOnMixedStatus(
    firstSelectionEntry: Pick<
      UserQualificationV2,
      'acquiredAt' | 'validFrom' | 'validTill' | 'confirmedByUser' | 'notes'
    >,
  ): void {
    this.acquiredAtFormControl.patchValue(
      this.selectionHasMixedValues.acquiredAt
        ? null
        : new Date(firstSelectionEntry.acquiredAt),
    );
    this.validFromFormControl.patchValue(
      this.selectionHasMixedValues.validFrom
        ? null
        : new Date(firstSelectionEntry.validFrom),
    );
    this.infiniteValidFormControl.patchValue(
      isInfiniteValidQualificationDateString(firstSelectionEntry.validTill) &&
        !this.selectionHasMixedValues.validTill
        ? true
        : null,
    );
    this.validUntilFormControl.patchValue(
      this.selectionHasMixedValues.validTill
        ? null
        : new Date(firstSelectionEntry.validTill),
    );
    this.confirmedByEmployeeFormControl.patchValue(
      this.selectionHasMixedValues.confirmedByUser
        ? null
        : firstSelectionEntry.confirmedByUser,
    );
    this.notesFormControl.patchValue(
      this.selectionHasMixedValues.notes ? null : firstSelectionEntry.notes,
    );
  }

  private setAttachmentsValues(
    qualifications: Pick<
      UserQualificationV2WithRelation,
      'userQualificationAttachments'
    >[],
  ): void {
    this.attachmentsList = [];
    if (
      qualifications.length === 1 &&
      qualifications[0].userQualificationAttachments?.length
    ) {
      qualifications[0].userQualificationAttachments.forEach((attachment) => {
        const attachmentToNzUploadFile: NzUploadFile = {
          uid: attachment.id,
          id: attachment.id,
          name: attachment.fileName,
          status: 'done',
          url: attachment.s3Path,
          userQualificationId: attachment.userQualificationId,
        };
        this.attachmentsList.push(attachmentToNzUploadFile);
      });
    }
    this.originalAttachmentsList = cloneDeep(this.attachmentsList);
  }

  private handleFailedDrawerSave(): Observable<undefined> {
    this.nzMessageService.error(this.translateService.instant('general.error'));

    timer(500)
      .pipe(take(1))
      .subscribe(() => {
        this.savingDrawer$.next(false);
      });
    return of(undefined);
  }

  private handleSuccessfulDrawerSave(): void {
    this.nzMessageService.success(
      this.translateService.instant('general.saved_successfully'),
    );
    this.bulkEditQualificationsService.triggerBulkEditedQualifications();
    this.savingDrawer$.next(false);
  }

  private setupSavingDrawerListener(): void {
    this.savingDrawer$
      .pipe(
        takeUntil(this.destroyed$),
        tap((saving) => {
          if (saving) {
            this.acquiredAtFormControl.disable();
            this.validFromFormControl.disable();
            this.validUntilFormControl.disable({
              emitEvent: false,
            });
            this.infiniteValidFormControl.disable({
              emitEvent: false,
            });
            this.confirmedByEmployeeFormControl.disable();
            this.notesFormControl.disable();
          } else {
            this.acquiredAtFormControl.enable();
            this.validFromFormControl.enable();
            this.validUntilFormControl.enable({
              emitEvent: false,
            });
            this.infiniteValidFormControl.enable({
              emitEvent: false,
            });
            this.confirmedByEmployeeFormControl.enable();
            this.notesFormControl.enable();
          }
        }),
      )
      .subscribe();
  }
}
